import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Activity,
  ClipboardCheck,
  CreditCard,
  DollarSign,
  FileText,
  MailCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSummary } from "../../hooks";
import BillTable from "../billTable/billTable";

// Define the same BillFilters interface as in BillTable
interface BillFilters {
  page?: number;
  limit?: number;
  search_data?: string;
  status?: string[];
  premise?: string[];
  consumer_type?: string[];
  bill_cycle?: string[];
  created_on_start?: string;
  created_on_end?: string;
  due_date_start?: string;
  due_date_end?: string;
  consumption_min?: number;
  consumption_max?: number;
  amount_min?: number;
  amount_max?: number;
  outstanding_min?: number;
  outstanding_max?: number;
}

const BillLanding = () => {
  const currency = useCurrency();
  const [searchParams] = useSearchParams();

  // Parse filters from URL parameters - same logic as BillTable
  const getFiltersFromUrl = (): BillFilters => {
    const urlFilters: BillFilters = {};

    // Parse search query
    const searchParam = searchParams.get("search");
    if (searchParam) {
      urlFilters.search_data = searchParam;
    }

    // Parse status array (multi-select)
    const statusParam = searchParams.get("status");
    if (statusParam && statusParam !== "ALL_STATUS") {
      urlFilters.status = statusParam.split(",");
    }

    // Parse premise type array (multi-select)
    const premiseParam = searchParams.get("premise");
    if (premiseParam && premiseParam !== "ALL_PREMISE_TYPES") {
      urlFilters.premise = premiseParam.split(",");
    }

    // Parse consumer type array (multi-select)
    const consumerParam = searchParams.get("consumer_type");
    if (consumerParam && consumerParam !== "ALL_CONSUMER_TYPES") {
      urlFilters.consumer_type = consumerParam.split(",");
    }

    // Parse bill cycle array (multi-select)
    const billCycleParam = searchParams.get("bill_cycle");
    if (billCycleParam && billCycleParam !== "ALL_BILL_CYCLES") {
      urlFilters.bill_cycle = billCycleParam.split(",");
    }

    // Parse date ranges
    const createdStartParam = searchParams.get("created_on_start");
    if (createdStartParam) {
      urlFilters.created_on_start = createdStartParam;
    }

    const createdEndParam = searchParams.get("created_on_end");
    if (createdEndParam) {
      urlFilters.created_on_end = createdEndParam;
    }

    const dueStartParam = searchParams.get("due_date_start");
    if (dueStartParam) {
      urlFilters.due_date_start = dueStartParam;
    }

    const dueEndParam = searchParams.get("due_date_end");
    if (dueEndParam) {
      urlFilters.due_date_end = dueEndParam;
    }

    // Parse numeric ranges
    const consumptionMinParam = searchParams.get("consumption_min");
    if (consumptionMinParam) {
      urlFilters.consumption_min = Number(consumptionMinParam);
    }

    const consumptionMaxParam = searchParams.get("consumption_max");
    if (consumptionMaxParam) {
      urlFilters.consumption_max = Number(consumptionMaxParam);
    }

    const amountMinParam = searchParams.get("amount_min");
    if (amountMinParam) {
      urlFilters.amount_min = Number(amountMinParam);
    }

    const amountMaxParam = searchParams.get("amount_max");
    if (amountMaxParam) {
      urlFilters.amount_max = Number(amountMaxParam);
    }

    const outstandingMinParam = searchParams.get("outstanding_min");
    if (outstandingMinParam) {
      urlFilters.outstanding_min = Number(outstandingMinParam);
    }

    const outstandingMaxParam = searchParams.get("outstanding_max");
    if (outstandingMaxParam) {
      urlFilters.outstanding_max = Number(outstandingMaxParam);
    }

    return urlFilters;
  };

  const [currentFilters, setCurrentFilters] =
    useState<BillFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    setCurrentFilters(getFiltersFromUrl());
  }, [searchParams]);

  // Convert filters to API format - clean up empty values
  const getApiFilters = (filters: BillFilters) => {
    const apiFilters: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            apiFilters[key] = value;
          }
        } else {
          apiFilters[key] = value;
        }
      }
    });

    return apiFilters;
  };

  const apiFilters = getApiFilters(currentFilters);

  // Use summary hook with current filters from URL
  // const { data: summaryData } = useSummary(apiFilters);

  // KPI Cards configuration for Billing Summary
  // const billingSummaryCards = [
  //   {
  //     title: "Total Bills",
  //     count: summaryData?.processedBills || 0,
  //     trendSummary: "Total number of bills processed",
  //     trendDirection: "up" as const,
  //     icon: <Activity className="h-8 w-8 text-blue-500" />,
  //   },
  //   {
  //     title: "Consumption",
  //     count: `${summaryData?.totalConsumption || 0} unit`,
  //     trendSummary: "Total consumption across all bills",
  //     trendDirection: "up" as const,
  //     icon: <FileText className="h-8 w-8 text-indigo-500" />,
  //   },
  //   {
  //     title: "Average Consumption",
  //     count: `${summaryData?.avgConsumption || 0} unit/day`,
  //     trendSummary: "Daily average consumption rate",
  //     trendDirection: "down" as const,
  //     icon: <MailCheck className="h-8 w-8 text-green-500" />,
  //   },
  //   {
  //     title: "Actual Bills",
  //     count: summaryData?.actualBills || 0,
  //     trendSummary: "Bills based on actual meter readings",
  //     trendDirection: "up" as const,
  //     icon: <ClipboardCheck className="h-8 w-8 text-purple-500" />,
  //   },
  //   {
  //     title: "Non-Actual Bills",
  //     count: summaryData?.nonActualBills || 0,
  //     trendSummary: "Bills based on estimated readings",
  //     trendDirection: "down" as const,
  //     icon: <FileText className="h-8 w-8 text-orange-500" />,
  //   },
  // ];

  // KPI Cards configuration for Financial Summary
  // const financialSummaryCards = [
  //   {
  //     title: "Billed Amount",
  //     count: `${currency} ${
  //       summaryData?.totalBillAmount?.toFixed(2) || "0.00"
  //     }`,
  //     trendSummary: "Total amount billed to consumers",
  //     trendDirection: "up" as const,
  //     icon: <DollarSign className="h-8 w-8 text-green-500" />,
  //   },
  //   {
  //     title: "Outstanding Amount",
  //     count: `${currency} ${
  //       summaryData?.outstandingAmount?.toFixed(2) || "0.00"
  //     }`,
  //     trendSummary: "Amount pending payment from consumers",
  //     trendDirection: "down" as const,
  //     icon: <DollarSign className="h-8 w-8 text-red-500" />,
  //   },
  //   {
  //     title: "Consumption Charges",
  //     count: `${currency} ${summaryData?.baseAmount?.toFixed(2) || "0.00"}`,
  //     trendSummary: "Revenue from utility consumption",
  //     trendDirection: "up" as const,
  //     icon: <CreditCard className="h-8 w-8 text-blue-500" />,
  //   },
  //   {
  //     title: "Service Charges",
  //     count: `${currency} ${summaryData?.serviceCharges?.toFixed(2) || "0.00"}`,
  //     trendSummary: "Revenue from service fees",
  //     trendDirection: "up" as const,
  //     icon: <CreditCard className="h-8 w-8 text-purple-500" />,
  //   },
  //   {
  //     title: "Late Fees",
  //     count: `${currency} ${summaryData?.latePaymentRevenue || "0.00"}`,
  //     trendSummary: "Revenue from late payment penalties",
  //     trendDirection: "down" as const,
  //     icon: <CreditCard className="h-8 w-8 text-amber-500" />,
  //   },
  // ];

  // Check if any filters are applied based on URL parameters
  const hasActiveFilters = () => {
    return (
      currentFilters.search_data ||
      (currentFilters.status && currentFilters.status.length > 0) ||
      (currentFilters.premise && currentFilters.premise.length > 0) ||
      (currentFilters.consumer_type &&
        currentFilters.consumer_type.length > 0) ||
      (currentFilters.bill_cycle && currentFilters.bill_cycle.length > 0) ||
      currentFilters.created_on_start ||
      currentFilters.created_on_end ||
      currentFilters.due_date_start ||
      currentFilters.due_date_end ||
      (currentFilters.consumption_min !== undefined &&
        currentFilters.consumption_min > 0) ||
      currentFilters.consumption_max !== undefined ||
      (currentFilters.amount_min !== undefined &&
        currentFilters.amount_min > 0) ||
      currentFilters.amount_max !== undefined ||
      (currentFilters.outstanding_min !== undefined &&
        currentFilters.outstanding_min > 0) ||
      currentFilters.outstanding_max !== undefined
    );
  };

  return (
    <div className="">
      <div className="space-y-8">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <h1 className="page-title">Master Bill Viewer</h1>
            <p className="page-subtitle">
              Monitor, search, and manage all utility bills in one place
            </p>
            {hasActiveFilters() && (
              <p className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-md inline-block w-fit">
                Showing filtered results based on your current search criteria
              </p>
            )}
          </div>

          {/* Billing Summary Section */}
          {/* <div className="flex flex-col gap-4"> */}
            {/* <h2 className="text-lg font-semibold">
              Billing Summary
              {hasActiveFilters() && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  (Filtered Data)
                </span>
              )}
            </h2> */}

            {/* Billing Summary Cards - Using loop with QuickStatsCard */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {billingSummaryCards.map((card, index) => (
                <QuickStatsCard
                  key={index}
                  title={card.title}
                  count={card.count}
                  trendSummary={card.trendSummary}
                  trendDirection={card.trendDirection}
                  icon={card.icon}
                />
              ))}
            </div> */}
          {/* </div> */}

          {/* Financial Summary Section */}
          {/* <div className="flex flex-col gap-4"> */}
            {/* <h2 className="text-lg font-semibold">
              Financial Summary
              {hasActiveFilters() && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  (Filtered Data)
                </span>
              )}
            </h2> */}

            {/* Financial Summary Cards - Using loop with QuickStatsCard */}
            {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {financialSummaryCards.map((card, index) => (
                <QuickStatsCard
                  key={index}
                  title={card.title}
                  count={card.count}
                  trendSummary={card.trendSummary}
                  trendDirection={card.trendDirection}
                  icon={card.icon}
                />
              ))}
            </div> */}
          {/* </div> */}
        </div>

        {/* Bill Table - Now self-contained with URL-based filters */}
        <BillTable className="w-full" />
      </div>
    </div>
  );
};

export default BillLanding;
